import DjangoMessages from 'components/DjangoMessages';
import Field from 'components/Form/Field';
import FieldDivider from 'components/Form/FieldDivider';
import Icon from 'components/Icon';
import Information from 'components/Information';
import KeyValue from 'components/Layout/KeyValue';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import SectionSwitcher from 'components/SectionSwitcher';
import TooltipWrapper from 'components/Tooltip';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import {H1} from 'components/Typography';
import Value from 'components/Value';
import {ComponentProps, FC} from 'react';
import {useBackend} from 'state/backend';
import {c, jt, t} from 'ttag';
import {attachmentTypeLabels} from 'utils/attachment-type';
import {mergePermissions} from 'utils/permissions';
import {setUrl} from 'utils/request';

import {DocumentReminderSettings} from '../Edit/DocumentReminders/types';
import {BackendData} from '../types';
import {sendingSettingsLinks} from '../utils';

const SendingMethods = () => {
    const {data} = useBackend<BackendData>();

    const value = [
        {label: t`Send via Client Inbox`, value: data.sendToClientinboxDefault},
        {label: t`Secure Send`, value: data.sendBySecureSendDefault},
        {label: t`Send by Post`, value: data.sendByPostDefault},
    ];
    if (data.isUsingPeppolSend) {
        value.push({
            label: t`Peppol Send`,
            value: data.sendByPeppolsendDefault,
        });
    }

    const link = (
        <Link
            href={sendingSettingsLinks.howToSendDocuments}
            isOpenInNewTab={true}
            key="help-center"
        >
            {t`Help Center`}
        </Link>
    );

    return (
        <>
            <Field label={t`Sending Methods`} type="value">
                <Value value={{type: 'boolean-group', value: value}} />
            </Field>
            <Field type="divider">
                <span
                    className="block flex-1 whitespace-pre-line pr-1 text-sm text-light-secondary"
                    role="note"
                >{jt`Send by Post requires the purchase of MailPoints. See the ${link} (Japanese) for more information on our Sending Methods.`}</span>
            </Field>
        </>
    );
};

const ClientInboxUserEditing = () => {
    const {data} = useBackend<BackendData>();

    return (
        <Field label={t`Client Inbox User Editing`} type="value">
            <Value
                value={{
                    label: t`Allow Client to Edit Client Inbox Users`,
                    type: 'boolean',
                    value: data.defaultAllowClientinboxUsersToEditUsers,
                }}
            />
        </Field>
    );
};

const PasswordProtection = () => {
    const {data} = useBackend<BackendData>();
    return (
        <Field label={t`Password Protection`} type="value">
            <Value
                value={{
                    label: t`Send Separate Password Email`,
                    type: 'boolean',
                    value: data.pwProtectPickupPages,
                }}
            />
        </Field>
    );
};

const MakeleapsPayments = () => {
    const {data} = useBackend<BackendData>();
    return (
        <Field label={t`MakeLeaps Payments`} type="value">
            <Value
                value={{
                    label: t`Enable MakeLeaps Payments`,
                    type: 'boolean',
                    value: data.enableCcPayments,
                }}
            />
        </Field>
    );
};

const ContactsForSecureSending = () => {
    const {data} = useBackend<BackendData>();
    return (
        <Field label={t`Contacts for Secure Sending`} type="value">
            <Value
                value={{
                    type: 'string',
                    value: data.useDocumentContact
                        ? t`Document's Contacts`
                        : t`Client's Contacts`,
                }}
            />
        </Field>
    );
};

const DefaultSecureSendSubject = () => {
    const {data} = useBackend<BackendData>();
    return (
        <>
            <Field label={t`Default Secure Send Subject`} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: data.securesendSubject || '—',
                    }}
                />
            </Field>
            <Field label={t`Default Secure Send Message`} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: data.securesendMessage || '—',
                    }}
                />
            </Field>
        </>
    );
};

const DocumentReminder: FC<{data: DocumentReminderSettings}> = ({data}) => {
    const labels = attachmentTypeLabels();
    const documentReminderHelpCenterLink = (
        <Link
            className="font-semibold"
            href={sendingSettingsLinks.documentReminderHelpLink}
            isOpenInNewTab={true}
            key="document-reminder-help-link"
        >{t`Help Center`}</Link>
    );
    const disabled = !data.isActive;
    return (
        <>
            <Field label={t`Document Reminder`} type="value">
                <Value
                    value={{
                        label: t`Enable Document Reminder`,
                        type: 'boolean',
                        value: data.isActive,
                    }}
                />
            </Field>
            <FieldDivider>{t`General Settings`}</FieldDivider>
            <Field label={t`Reminder Timing`} type="value">
                <Value
                    value={{
                        disabled,
                        type: 'string',
                        value: t`${data.timing} Days Later`,
                    }}
                />
            </Field>
            <Field
                label={
                    <div className="flex flex-row items-center">
                        {t`Attachment Types`}
                        <TooltipIcon
                            aria-label="simple-field-tooltip"
                            className="ml-1"
                            message={t`Attachment types that require Reminders`}
                            size="base"
                        />
                    </div>
                }
                type="value"
            >
                <Value
                    value={{
                        disabled,
                        type: 'tags',
                        value: data.attachmentTypes.map((type) => ({
                            color: data.isActive ? 'blue' : 'gray',
                            label: labels[type],
                        })),
                    }}
                />
            </Field>
            <FieldDivider>{t`Secure Send Reminder Email Settings`}</FieldDivider>
            <SectionSwitcher className="col-span-2">
                <SectionSwitcher.Section label={t`Japanese Email`}>
                    <KeyValue keyWidth={13}>
                        <Field label={t`Reminder Email Subject`} type="value">
                            <Value
                                value={{
                                    disabled,
                                    type: 'string',
                                    value: data.secureSendSubjectJa || 'ー',
                                }}
                            />
                        </Field>
                        <Field label={t`Reminder Email Body`} type="value">
                            <Value
                                value={{
                                    disabled,
                                    type: 'string',
                                    value: data.secureSendMessageJa || 'ー',
                                }}
                            />
                        </Field>
                    </KeyValue>
                </SectionSwitcher.Section>
                <SectionSwitcher.Section label={t`English Email`}>
                    <KeyValue keyWidth={13}>
                        <Field label={t`Reminder Email Subject`} type="value">
                            <Value
                                value={{
                                    disabled,
                                    type: 'string',
                                    value: data.secureSendSubjectEn || 'ー',
                                }}
                            />
                        </Field>
                        <Field label={t`Reminder Email Body`} type="value">
                            <Value
                                value={{
                                    disabled,
                                    type: 'string',
                                    value: data.secureSendMessageEn || 'ー',
                                }}
                            />
                        </Field>
                    </KeyValue>
                </SectionSwitcher.Section>
            </SectionSwitcher>
            <FieldDivider>{t`Client Inbox Reminder Email Settings`}</FieldDivider>
            <Field type="divider">
                <span
                    className="block flex-1 whitespace-pre-line pr-1 text-sm text-light-secondary"
                    role="note"
                >
                    {jt`Currently, Client Inbox reminders are not customizable. For more information, see the ${documentReminderHelpCenterLink} (Japanese site).`}
                </span>
            </Field>
        </>
    );
};

const PeppolSend = () => {
    const {data} = useBackend<BackendData>();

    if (!data.isUsingPeppolSend) return null;

    return (
        <>
            <Field label={t`Bank Account Number`} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: data.peppolBankAccount || '—',
                    }}
                />
            </Field>
            <Field label={t`Bank Account Name`} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: data.peppolBankAccountName || '—',
                    }}
                />
            </Field>
        </>
    );
};

const DocumentArchive = () => {
    const {data} = useBackend<BackendData>();
    const createDocumentArchiveForMarkedAsSent =
        data.createDocumentArchiveForMarkedAsSent || false;

    return (
        <Field
            label={t`Default when updating Document Sent Date`}
            tooltip={{
                message: t`You can set a default for Document Archive Creation when you update the Document Sent Date via UI.`,
            }}
            type="value"
        >
            <Value
                value={{
                    type: 'custom',
                    value: (
                        <div className="flex flex-col gap-2">
                            <div className="inline-flex items-center gap-2">
                                <Icon
                                    icon={
                                        createDocumentArchiveForMarkedAsSent
                                            ? 'enabled_checkbox'
                                            : 'disabled_checkbox'
                                    }
                                    size="base"
                                />
                                {t`Create Document Archive`}
                            </div>
                            {data.manualArchiveEnabled.allowed &&
                                createDocumentArchiveForMarkedAsSent && (
                                    <div className="inline-flex items-center gap-2 pl-6">
                                        {data.defaultMarkedAsSentArchiveSource ===
                                            'manually_sent_electronically' &&
                                            t`Sent electronically outside MakeLeaps`}
                                        {data.defaultMarkedAsSentArchiveSource ===
                                            'manually_sent_physically' &&
                                            t`Sent paper copy outside MakeLeaps`}
                                        {!data.defaultMarkedAsSentArchiveSource &&
                                            t`No default sending method selected`}
                                    </div>
                                )}
                        </div>
                    ),
                }}
            />
        </Field>
    );
};
const EmailSettings = () => {
    const {data, flags} = useBackend<BackendData>();

    const link = (
        <Link
            href={sendingSettingsLinks.customEmailDomain}
            isOpenInNewTab={true}
            key="help-center"
        >
            {t`Help Center`}
        </Link>
    );

    const productHCLink = (
        <Link
            href={sendingSettingsLinks.upgradeCustomEmailView}
            isOpenInNewTab={true}
            key="product-intro"
        >
            {t`product intro`}
        </Link>
    );

    const customEmailDomainButtonPermission = mergePermissions(
        data.permissions.companySettingsCanEdit,
        data.permissions.canManageDomains,
    );

    const customEmailDomainHelptext = data.features.customDomain
        ? jt`Send emails from your company's email domain(s). See the ${link} (Japanese) for more information.`
        : jt`Send emails from your company's email domain(s). Learn more in the ${productHCLink}.`;

    const showSenderNames = flags.customize_email_sender === true;

    return (
        <>
            <Field
                helpText={t`Set a company contact as the default Reply-To address for outgoing emails.`}
                label={t`Default Reply-To Address`}
                type="value"
            >
                <Value
                    value={{
                        type: 'custom',
                        value: (
                            <div className="flex flex-1 flex-col gap-4">
                                <div className="inline-flex items-center">
                                    <div className="flex-1">
                                        <div className="wrap-mail-text inline-flex items-center">
                                            {data.defaultReplyToContact
                                                ?.label || '—'}
                                        </div>
                                    </div>
                                    <div className="h-full">
                                        <TooltipWrapper
                                            message={
                                                data.permissions
                                                    .companySettingsCanEdit
                                                    .failureReason
                                            }
                                        >
                                            <LinkButton
                                                disabled={
                                                    !data.permissions
                                                        .companySettingsCanEdit
                                                        .allowed
                                                }
                                                href={
                                                    sendingSettingsLinks.documentEmailSettingsEditView
                                                }
                                                icon="edit"
                                                kind="secondary"
                                                size="xs"
                                            >{t`Edit`}</LinkButton>
                                        </TooltipWrapper>
                                    </div>
                                </div>
                            </div>
                        ),
                    }}
                />
            </Field>
            {showSenderNames && (
                <>
                    <Field
                        label={t`Sender/Reply-to Name (Japanese)`}
                        type="value"
                    >
                        <Value
                            value={{
                                type: 'string',
                                value: data.senderNames?.ja || '',
                            }}
                        ></Value>
                    </Field>
                    <Field
                        label={t`Sender/Reply-to Name (English)`}
                        type="value"
                    >
                        <Value
                            value={{
                                type: 'string',
                                value: data.senderNames?.en || '',
                            }}
                        ></Value>
                    </Field>
                </>
            )}
            <Field label={t`Custom Email Domains`} type="value">
                <Value
                    value={{
                        type: 'custom',
                        value: (
                            <div className="flex flex-1 flex-col gap-4">
                                <div className="inline-flex items-center">
                                    <div className="flex-1">
                                        {data.verifiedCustomDomains.length ===
                                        0 ? (
                                            '—'
                                        ) : (
                                            <div className="wrap-mail-text flex flex-col gap-2">
                                                {data.verifiedCustomDomains.map(
                                                    (domain) => (
                                                        <div key={domain}>
                                                            {domain}
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="h-full">
                                        <TooltipWrapper
                                            message={
                                                customEmailDomainButtonPermission.failureReason
                                            }
                                        >
                                            <LinkButton
                                                disabled={
                                                    !customEmailDomainButtonPermission.allowed
                                                }
                                                href={
                                                    sendingSettingsLinks.partnerCustomEmailDomainListView
                                                }
                                                kind="secondary"
                                                size="xs"
                                            >{t`Manage Custom Domains`}</LinkButton>
                                        </TooltipWrapper>
                                    </div>
                                </div>
                            </div>
                        ),
                    }}
                />
            </Field>
            <Field type="divider">
                <span
                    className="block flex-1 whitespace-pre-line pr-1 text-sm text-light-secondary"
                    role="note"
                >
                    {customEmailDomainHelptext}
                </span>
            </Field>
        </>
    );
};

const DxSettings = () => {
    const {data} = useBackend<BackendData>();

    const {dxSettings} = data;
    const instructions = dxSettings ? dxSettings.instructions : null;

    const targetDisplayName = (
        value: NonNullable<BackendData['data']['dxSettings']>['target'],
    ) => {
        switch (value) {
            case 'clientinbox_user':
                return t`Client Inbox User`;
            case 'default_contact':
                return t`Default Contact`;
            default:
                return '-';
        }
    };

    return (
        <>
            <Field label={t`Enable Email Address Collection`} type="value">
                <Value
                    value={{
                        type: 'boolean',
                        value: dxSettings ? dxSettings.defaultEnabled : false,
                    }}
                />
            </Field>
            {dxSettings && (
                <>
                    <Field label={t`Where Email Address is saved`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: targetDisplayName(dxSettings.target),
                            }}
                        />
                    </Field>
                    <FieldDivider>
                        {t`Email Address Registration Request`}
                        <TooltipIcon
                            aria-label="simple-field-tooltip"
                            href={sendingSettingsLinks.dxNoticeHelpCenterLink}
                            message={t`A request document that will be included in the envelope, containing your customized message, registration website information, and the Client's Registration Code.`}
                            size="base"
                        />
                    </FieldDivider>
                    <Field
                        label={
                            <div className="flex items-center gap-2">
                                {c('dx-helper').t`Title`}
                                <TooltipIcon
                                    href="https://help.makeleaps.jp/ja/articles/4108694#h_b385b3699f"
                                    message={t`This title also appears on the envelope's Cover Letter as the name of the document.`}
                                    size="base"
                                />
                            </div>
                        }
                        type="value"
                    >
                        <Value
                            value={{
                                type: 'string',
                                value: dxSettings.noticeTitle || '—',
                            }}
                        />
                    </Field>
                    <Field label={c('dx-helper').t`Message`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: dxSettings.noticeBody || '—',
                            }}
                        />
                    </Field>
                    <FieldDivider>
                        {t`Email Registration Instructions`}
                        <TooltipIcon
                            aria-label="simple-field-tooltip"
                            href={`tooltip-link-TBD`}
                            message={t`DX Instructions Tooltip`}
                            size="base"
                        />
                    </FieldDivider>
                    <Field
                        label={c('dx-helper').t`Instructions PDF`}
                        type="value"
                    >
                        <Value
                            className={
                                instructions && instructions.filename
                                    ? 'font-semibold text-green-700'
                                    : ''
                            }
                            value={{
                                type: 'custom',
                                value: instructions ? (
                                    <Link
                                        className="text-wrap"
                                        href={window.Urls.api_dx_partner_settings_instructions_download_view(
                                            window.HatsConstants.partner,
                                        )}
                                        key={instructions?.filename}
                                        style={{overflowWrap: 'anywhere'}}
                                    >
                                        {instructions?.filename}
                                    </Link>
                                ) : (
                                    <span>—</span>
                                ),
                            }}
                        ></Value>
                    </Field>
                    <Field
                        label={t`Include Instructions in the Envelope`}
                        tooltip={{
                            href: 'https://example.org',
                            message: t`Even if not included in the envelope, Clients will be able to download only from registration screens or emails.`,
                        }}
                        type="value"
                    >
                        <Value
                            value={{
                                type: 'boolean',
                                value: dxSettings
                                    ? dxSettings.attachInstructions
                                    : false,
                            }}
                        />
                    </Field>
                    <FieldDivider>
                        {c('dx-helper').t`"Registration Complete" Email`}
                    </FieldDivider>
                    <Field label={c('dx-helper').t`Message`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: dxSettings.confirmationEmailBody || '—',
                            }}
                        />
                    </Field>
                </>
            )}
        </>
    );
};

const Page = () => {
    const {data, flags} = useBackend<BackendData>();
    const showDxSettings = flags.mep098_dx_helper === true;
    const showDxInactiveAlert =
        data.dxSettings && !data.dxSettings.defaultEnabled;

    const clientListLink = (
        <a
            className="font-semibold"
            href={sendingSettingsLinks.clientListView}
            key="client-list"
        >{t`client list`}</a>
    );

    const documentReminderHelpCenterLink = (
        <Link
            className="font-semibold"
            href={sendingSettingsLinks.documentReminderHelpLink}
            isOpenInNewTab={true}
            key="document-reminder-help-link"
        >{t`Help Center`}</Link>
    );

    const editDocumentSendingLink: ComponentProps<
        typeof SectionHeader
    >['link'] = {
        disabled: !data.permissions.companySettingsCanEdit.allowed,
        icon: 'edit',
        kind: 'secondary',
        label: t`Edit`,
        onClick: data.permissions.companySettingsCanEdit.allowed
            ? () => setUrl(sendingSettingsLinks.documentSendingSettingsEdit)
            : () => {},
    };

    const editDocumentReminderLink: ComponentProps<
        typeof SectionHeader
    >['link'] = {
        disabled:
            !data.permissions.planDocumentReminder.allowed ||
            !data.permissions.companySettingsCanEdit.allowed,
        icon: 'edit',
        kind: 'secondary',
        label: t`Edit`,
        onClick:
            data.permissions.planDocumentReminder.allowed &&
            data.permissions.companySettingsCanEdit.allowed
                ? () =>
                      setUrl(sendingSettingsLinks.documentReminderSettingsEdit)
                : () => {},
    };

    const editPeppolSendLink: ComponentProps<typeof SectionHeader>['link'] = {
        disabled: !data.permissions.companySettingsCanEdit.allowed,
        icon: 'edit',
        kind: 'secondary',
        label: t`Edit`,
        onClick: data.permissions.companySettingsCanEdit.allowed
            ? () => setUrl(sendingSettingsLinks.partnerPeppolEditView)
            : () => {},
    };

    const editDocumentArchiveLink: ComponentProps<
        typeof SectionHeader
    >['link'] = {
        disabled: !data.permissions.companySettingsCanEdit.allowed,
        icon: 'edit',
        kind: 'secondary',
        label: t`Edit`,
        onClick: data.permissions.companySettingsCanEdit.allowed
            ? () => setUrl(sendingSettingsLinks.documentArchiveEditView)
            : () => {},
    };

    const editDxPartnerSettingsLink: ComponentProps<
        typeof SectionHeader
    >['link'] = {
        disabled: !data.permissions.companySettingsCanEdit.allowed,
        icon: 'edit',
        kind: 'secondary',
        label: t`Edit`,
        onClick: data.permissions.companySettingsCanEdit.allowed
            ? () => setUrl(sendingSettingsLinks.dxSettingsEditView)
            : () => {},
    };

    const dxHelperHelpCenterLink = (
        <Link
            href={sendingSettingsLinks.dxHelperHelpCenterLink}
            isOpenInNewTab={true}
            key="help-center"
        >{t`Help Center`}</Link>
    );

    return (
        <div>
            <DjangoMessages className="mb-4" />
            <NavigationTabs.Partner selection="sending_settings" />
            <div>
                <H1>{t`Sending Settings`}</H1>
                <SectionHeader
                    level={2}
                    link={editDocumentSendingLink}
                    subtitle={jt`These settings are used as the default values when adding new clients. You can see or edit these settings for existing clients in the ${clientListLink}.`}
                    title={t`Document Sending`}
                    tooltipMessage={
                        data.permissions.companySettingsCanEdit.failureReason
                    }
                />
                <KeyValue className="mt-4" keyWidth={14}>
                    <SendingMethods />
                    <ClientInboxUserEditing />
                    <PasswordProtection />
                    <MakeleapsPayments />
                    <ContactsForSecureSending />
                    <DefaultSecureSendSubject />
                </KeyValue>
                {data.documentReminderSettings && (
                    <>
                        <SectionHeader
                            level={2}
                            link={editDocumentReminderLink}
                            subtitle={
                                <>
                                    <div>{t`Automatically send out Document Reminder Emails to your clients when certain attachments of their Sending Orders remain unopened.`}</div>
                                    <div>{jt`See the ${documentReminderHelpCenterLink} (Japanese site) for more information.`}</div>
                                </>
                            }
                            title={t`Document Reminder`}
                            tooltipButtonText={
                                !data.permissions.planDocumentReminder.allowed
                                    ? t`Upgrade`
                                    : undefined
                            }
                            tooltipHref={
                                !data.permissions.planDocumentReminder.allowed
                                    ? 'https://www.makeleaps.jp/pricing/'
                                    : undefined
                            }
                            tooltipMessage={
                                !data.permissions.planDocumentReminder.allowed
                                    ? t`You do not have access to this feature at this plan level.`
                                    : data.permissions.companySettingsCanEdit
                                          .failureReason
                            }
                        />
                        {data.showDocumentReminderSettingsWarning && (
                            <Information
                                className="mt-4"
                                icon="warning"
                                size="sm"
                                type="alert"
                            >
                                {t`The feature is still off. You have saved settings, but the feature is not functional.`}
                            </Information>
                        )}
                        <KeyValue className="mt-4" keyWidth={14}>
                            <DocumentReminder
                                data={data.documentReminderSettings}
                            />
                        </KeyValue>
                    </>
                )}
                {data.isUsingPeppolSend && (
                    <>
                        <SectionHeader
                            level={2}
                            link={editPeppolSendLink}
                            title={t`Peppol Send`}
                            tooltipMessage={
                                data.permissions.companySettingsCanEdit
                                    .failureReason
                            }
                        />
                        <KeyValue className="mt-4" keyWidth={14}>
                            <PeppolSend />
                        </KeyValue>
                    </>
                )}
                <SectionHeader
                    level={2}
                    link={editDocumentArchiveLink}
                    title={t`Document Archive`}
                    tooltipMessage={
                        data.permissions.companySettingsCanEdit.failureReason
                    }
                />
                <KeyValue className="mt-4" keyWidth={14}>
                    <DocumentArchive />
                </KeyValue>
                <SectionHeader
                    level={2}
                    title={c('document-settings').t`Email Settings`}
                    tooltipMessage={
                        data.permissions.companySettingsCanEdit.failureReason
                    }
                />
                <KeyValue className="mt-4" keyWidth={14}>
                    <EmailSettings />
                </KeyValue>
                {showDxSettings && (
                    <>
                        <SectionHeader
                            level={2}
                            link={editDxPartnerSettingsLink}
                            subtitle={jt`Collect email addresses from Clients so you can transition to electronic sending methods. For details, see the ${dxHelperHelpCenterLink} (Japanese site).`}
                            title={t`Email Address Collection`}
                        />
                        {showDxInactiveAlert && (
                            <Information
                                className="mt-4"
                                icon="warning"
                                size="sm"
                                type="alert"
                            >
                                {t`Email Collection is currently off.`}
                            </Information>
                        )}
                        <KeyValue className="mt-4" keyWidth={14}>
                            <DxSettings />
                        </KeyValue>
                    </>
                )}
            </div>
        </div>
    );
};
export default Page;
